import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Select,
  Card,
  CardBody,
  Icon,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Progress,
  Flex,
  IconButton,
  Input,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
} from '@chakra-ui/react';
import {
  PaperPlaneTilt,
  Buildings,
  CloudArrowUp,
  File,
  X,
  CheckCircle,
  XCircle,
  ArrowLeft,
  CalendarBlank,
  Clock,
  ArrowsClockwise,
  FileText,
  Bank,
  Warning,
  WarningCircle,
} from 'phosphor-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import SecaoClienteLayout from '../layout';
import { api } from '../../../services/api';
import { formatCNPJ } from '../../../utils/FormatFunctions';

interface IEmpresa {
  id: number;
  external_id: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string | null;
}

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: IEmpresa[];
}

interface FileUpload {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'success' | 'error';
  taskId?: string;
  errorMessage?: string;
  mesReferencia: string; // formato YYYY-MM
}

interface UploadResponse {
  data: {
    task_id: string;
    status: string;
    arquivo_nome: string;
    message: string;
  };
  message: string;
}

interface UploadItem {
  task_id: string;
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'REVOKED';
  nome_arquivo: string;
  tipo: string;
  progress: number | null;
  result: any;
  error: string | null;
  created_at: string;
}

interface UploadListResponse {
  data: UploadItem[];
  message: string;
}

interface ExtratoMesAnterior {
  nome: string;
  enviado_mes_atual: boolean;
}

interface ExtratosResponse {
  extratos: ExtratoMesAnterior[];
  mes_referencia: number;
  ano_referencia: number;
}

const DocumentosEmpresa = () => {
  const navigate = useNavigate();
  const { empresaId } = useParams<{ empresaId: string }>();

  // Pegar dados do cliente do localStorage
  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  // Encontrar empresa atual
  const empresaAtual = useMemo(() => {
    if (!clienteData?.empresas || !empresaId) return null;
    return clienteData.empresas.find(emp => emp.external_id === empresaId);
  }, [clienteData?.empresas, empresaId]);

  const [files, setFiles] = useState<FileUpload[]>([]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadsList, setUploadsList] = useState<UploadItem[]>([]);
  const [isLoadingUploads, setIsLoadingUploads] = useState(false);
  const [extratosData, setExtratosData] = useState<ExtratosResponse | null>(null);
  const [extratosLoading, setExtratosLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Função para buscar uploads da empresa
  const fetchUploads = useCallback(async () => {
    if (!empresaAtual) return;

    setIsLoadingUploads(true);
    try {
      const token = localStorage.getItem('cliente_access_token');
      const response = await api.get<UploadListResponse>(
        `/api/v1/uploads/?empresa_id=${empresaAtual.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setUploadsList(response.data.data || []);
    } catch (error) {
      // Silenciosamente falha - não é crítico
      setUploadsList([]);
    } finally {
      setIsLoadingUploads(false);
    }
  }, [empresaAtual]);

  // Buscar uploads apenas uma vez ao carregar a página
  useEffect(() => {
    if (empresaAtual) {
      fetchUploads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaAtual?.id]);

  // Buscar extratos pendentes do mês anterior
  useEffect(() => {
    const fetchExtratos = async () => {
      if (!empresaAtual || !clienteData?.id) return;
      setExtratosLoading(true);
      try {
        const token = localStorage.getItem('cliente_access_token');
        const response = await api.get(
          `/api/v1/secao-cliente/dossie/${empresaAtual.external_id}/extratos-mes-anterior/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setExtratosData(response.data.data);
      } catch {
        setExtratosData(null);
      } finally {
        setExtratosLoading(false);
      }
    };
    fetchExtratos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaAtual?.external_id, clienteData?.id]);

  const handleMesReferenciaChange = (fileId: string, value: string) => {
    setFiles(prev => prev.map(f => (f.id === fileId ? { ...f, mesReferencia: value } : f)));
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles = Array.from(selectedFiles).map(file => ({
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      progress: 0,
      status: 'pending' as const,
      mesReferencia: '',
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Upload de um arquivo individual
  const uploadFile = async (fileUpload: FileUpload, index: number): Promise<boolean> => {
    const token = localStorage.getItem('cliente_access_token');

    // Atualizar status para uploading
    setFiles(prev =>
      prev.map((f, idx) =>
        idx === index ? { ...f, status: 'uploading' as const, progress: 0 } : f,
      ),
    );

    try {
      const formData = new FormData();
      formData.append('file', fileUpload.file);

      if (empresaAtual) {
        formData.append('empresa_id', String(empresaAtual.id));
        formData.append('external_id', empresaAtual.external_id);
      }

      formData.append('tipo', tipoDocumento);
      const [ano, mes] = fileUpload.mesReferencia.split('-');
      formData.append('ano', ano);
      formData.append('mes', mes);

      // Fazer upload
      const response = await api.post<UploadResponse>('/api/v1/uploads/', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: progressEvent => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setFiles(prev =>
            prev.map((f, idx) => (idx === index ? { ...f, progress: percentCompleted } : f)),
          );
        },
      });

      const taskId = response.data.data.task_id;

      // Atualizar status para processing e salvar taskId
      setFiles(prev =>
        prev.map((f, idx) =>
          idx === index ? { ...f, status: 'processing' as const, taskId, progress: 100 } : f,
        ),
      );

      // Upload realizado com sucesso - não precisa aguardar processamento do Celery
      // O arquivo já foi enviado para o S3 e a task foi criada
      setFiles(prev =>
        prev.map((f, idx) =>
          idx === index ? { ...f, status: 'success' as const, progress: 100 } : f,
        ),
      );

      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao enviar arquivo';
      setFiles(prev =>
        prev.map((f, idx) =>
          idx === index ? { ...f, status: 'error' as const, errorMessage } : f,
        ),
      );
      return false;
    }
  };

  const handleEnviar = async () => {
    if (!tipoDocumento) {
      toast.error('Selecione o tipo de documento');
      return;
    }

    if (files.length === 0) {
      toast.error('Adicione pelo menos um arquivo');
      return;
    }

    const arquivosSemReferencia = files.some(f => !f.mesReferencia);
    if (arquivosSemReferencia) {
      toast.error('Informe o mês de referência para todos os arquivos');
      return;
    }

    setIsUploading(true);

    let successCount = 0;
    let errorCount = 0;

    // Processar arquivos sequencialmente
    for (let i = 0; i < files.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const success = await uploadFile(files[i], i);
      if (success) {
        successCount += 1;
      } else {
        errorCount += 1;
      }
    }

    setIsUploading(false);

    if (errorCount === 0) {
      toast.success(`${successCount} documento(s) enviado(s) com sucesso!`);
      // Atualizar lista de uploads
      fetchUploads();
      // Limpar após sucesso
      setTimeout(() => {
        setFiles([]);
        setTipoDocumento('');
        onClose();
      }, 1500);
    } else if (successCount > 0) {
      toast.warning(`${successCount} enviado(s), ${errorCount} com erro`);
      // Atualizar lista mesmo com erros parciais
      fetchUploads();
    } else {
      toast.error('Falha ao enviar os documentos');
    }
  };

  const handleCloseModal = () => {
    if (!isUploading) {
      setFiles([]);
      setTipoDocumento('');
      onClose();
    }
  };

  const getFileStatusIcon = (fileUpload: FileUpload) => {
    switch (fileUpload.status) {
      case 'success':
        return <Icon as={CheckCircle} color="green.500" boxSize={5} />;
      case 'error':
        return <Icon as={XCircle} color="red.500" boxSize={5} />;
      case 'uploading':
      case 'processing':
        return <Spinner size="sm" color="green.500" />;
      default:
        return null;
    }
  };

  const getFileStatusText = (fileUpload: FileUpload) => {
    switch (fileUpload.status) {
      case 'uploading':
        return 'Enviando...';
      case 'processing':
        return 'Processando...';
      case 'success':
        return 'Enviado!';
      case 'error':
        return fileUpload.errorMessage || 'Erro';
      default:
        return '';
    }
  };

  // Se não encontrou a empresa
  if (!empresaAtual) {
    return (
      <SecaoClienteLayout>
        <VStack spacing={6} align="stretch">
          <Card variant="outline" bg="gray.50">
            <CardBody>
              <VStack py={8} spacing={4}>
                <Icon as={Buildings} boxSize={12} color="gray.300" />
                <Text color="gray.500" textAlign="center">
                  Empresa não encontrada
                </Text>
                <Button
                  variant="outline"
                  leftIcon={<ArrowLeft size={18} />}
                  onClick={() => navigate('/secao-cliente/documentos')}
                >
                  Voltar
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </SecaoClienteLayout>
    );
  }

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
          <HStack spacing={3}>
            <Box
              p={3}
              borderRadius="xl"
              bgGradient="linear(to-br, green.400, green.600)"
              boxShadow="lg"
            >
              <Icon as={PaperPlaneTilt} boxSize={6} color="white" weight="duotone" />
            </Box>
            <Box>
              <Heading size="lg" color="gray.800">
                Envio de Documentos
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Envie documentos para o escritório de contabilidade
              </Text>
            </Box>
          </HStack>

          <Button
            variant="outline"
            leftIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/secao-cliente/documentos')}
          >
            Voltar
          </Button>
        </Flex>

        {/* Empresa selecionada */}
        <Card variant="outline" bg="green.50" borderColor="green.200">
          <CardBody>
            <HStack spacing={3}>
              <Box p={2} borderRadius="lg" bg="green.100">
                <Icon as={Buildings} boxSize={5} color="green.600" />
              </Box>
              <Box flex={1}>
                <Text fontWeight="600" color="gray.800">
                  {empresaAtual.razao_social}
                </Text>
                {empresaAtual.cnpj && (
                  <Text fontSize="sm" color="gray.600">
                    {formatCNPJ(empresaAtual.cnpj)}
                  </Text>
                )}
              </Box>
            </HStack>
          </CardBody>
        </Card>

        {/* Extratos Pendentes */}
        {extratosLoading && (
          <Card variant="outline" borderColor="gray.200" bg="gray.50">
            <CardBody>
              <Flex justify="center" py={6}>
                <Spinner size="lg" color="green.500" />
              </Flex>
            </CardBody>
          </Card>
        )}
        {!extratosLoading &&
          extratosData &&
          extratosData.extratos.length > 0 &&
          (() => {
            const pendentes = extratosData.extratos.filter(e => !e.enviado_mes_atual);
            const enviados = extratosData.extratos.filter(e => e.enviado_mes_atual);
            const mesLabel = new Date(
              extratosData.ano_referencia,
              extratosData.mes_referencia - 1,
            ).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

            return (
              <Card
                variant="outline"
                borderColor={pendentes.length > 0 ? 'orange.200' : 'green.200'}
                bg={pendentes.length > 0 ? 'orange.50' : 'green.50'}
              >
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <HStack justify="space-between" flexWrap="wrap" gap={2}>
                      <HStack spacing={3}>
                        <Box
                          p={2}
                          borderRadius="lg"
                          bg={pendentes.length > 0 ? 'orange.100' : 'green.100'}
                        >
                          <Icon
                            as={Bank}
                            boxSize={5}
                            color={pendentes.length > 0 ? 'orange.600' : 'green.600'}
                            weight="duotone"
                          />
                        </Box>
                        <Box>
                          <Text fontWeight="600" color="gray.800">
                            Extratos Bancários Pendentes
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Referência: {mesLabel}
                          </Text>
                        </Box>
                      </HStack>
                      {pendentes.length > 0 && (
                        <Badge colorScheme="orange" variant="subtle" fontSize="sm" px={3} py={1}>
                          <HStack spacing={1}>
                            <Icon as={WarningCircle} />
                            <Text>
                              {pendentes.length} pendente{pendentes.length > 1 ? 's' : ''}
                            </Text>
                          </HStack>
                        </Badge>
                      )}
                      {pendentes.length === 0 && (
                        <Badge colorScheme="green" variant="subtle" fontSize="sm" px={3} py={1}>
                          <HStack spacing={1}>
                            <Icon as={CheckCircle} />
                            <Text>Tudo enviado</Text>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>

                    {pendentes.length > 0 && (
                      <Box
                        bg="white"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="orange.200"
                        p={3}
                      >
                        <HStack spacing={2} mb={3}>
                          <Icon as={Warning} color="orange.500" boxSize={4} />
                          <Text fontSize="sm" fontWeight="500" color="orange.700">
                            Você precisa enviar os extratos dos seguintes bancos:
                          </Text>
                        </HStack>
                        <VStack spacing={2} align="stretch">
                          {pendentes.map(extrato => (
                            <HStack
                              key={extrato.nome}
                              p={3}
                              bg="orange.50"
                              borderRadius="md"
                              justify="space-between"
                            >
                              <HStack spacing={3}>
                                <Icon as={Bank} boxSize={4} color="orange.500" />
                                <Text fontSize="sm" fontWeight="600" color="gray.800">
                                  {extrato.nome}
                                </Text>
                              </HStack>
                              <Badge colorScheme="orange" variant="outline" fontSize="xs">
                                Pendente
                              </Badge>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    {enviados.length > 0 && (
                      <Box
                        bg="white"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="green.200"
                        p={3}
                      >
                        <Text fontSize="sm" fontWeight="500" color="green.700" mb={2}>
                          Extratos já enviados:
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {enviados.map(extrato => (
                            <HStack
                              key={extrato.nome}
                              p={3}
                              bg="green.50"
                              borderRadius="md"
                              justify="space-between"
                            >
                              <HStack spacing={3}>
                                <Icon as={Bank} boxSize={4} color="green.500" />
                                <Text fontSize="sm" fontWeight="600" color="gray.800">
                                  {extrato.nome}
                                </Text>
                              </HStack>
                              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                <HStack spacing={1}>
                                  <Icon as={CheckCircle} />
                                  <Text>Enviado</Text>
                                </HStack>
                              </Badge>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })()}

        {/* Área de envio */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={CloudArrowUp} boxSize={5} color="green.500" />
                  <Text fontWeight="600" color="gray.700">
                    Enviar arquivos
                  </Text>
                </HStack>
                <Button colorScheme="green" leftIcon={<CloudArrowUp size={20} />} onClick={onOpen}>
                  Enviar Documentos
                </Button>
              </HStack>
              <Text color="gray.500" fontSize="sm">
                Clique no botão &quot;Enviar Documentos&quot; para selecionar e enviar seus
                arquivos. Os documentos serão processados automaticamente pelo escritório.
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {/* Histórico de Uploads */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FileText} boxSize={5} color="green.500" />
                  <Text fontWeight="600" color="gray.700">
                    Documentos Enviados
                  </Text>
                </HStack>
                <Tooltip label="Atualizar lista">
                  <IconButton
                    aria-label="Atualizar"
                    icon={<ArrowsClockwise size={18} />}
                    size="sm"
                    variant="ghost"
                    onClick={fetchUploads}
                    isLoading={isLoadingUploads}
                  />
                </Tooltip>
              </HStack>

              {isLoadingUploads && (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="green.500" />
                </Flex>
              )}

              {!isLoadingUploads && uploadsList.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Icon as={FileText} boxSize={10} color="gray.300" mb={3} />
                  <Text color="gray.500">Nenhum documento enviado ainda</Text>
                </Box>
              )}

              {!isLoadingUploads && uploadsList.length > 0 && (
                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Arquivo</Th>
                        <Th>Tipo</Th>
                        <Th>Status</Th>
                        <Th>Data</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {uploadsList.map(upload => {
                        const tipoLabels: Record<string, string> = {
                          departamento_pessoal: 'Dept. Pessoal',
                          extratos_bancarios: 'Extratos',
                          documentos_onboard: 'On Board',
                        };
                        const tipoLabel = tipoLabels[upload.tipo] || upload.tipo;

                        return (
                          <Tr key={upload.task_id}>
                            <Td>
                              <HStack>
                                <Icon as={File} color="gray.500" />
                                <Text fontSize="sm" noOfLines={1} maxW="200px">
                                  {upload.nome_arquivo}
                                </Text>
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600">
                                {tipoLabel}
                              </Text>
                            </Td>
                            <Td>
                              {upload.status === 'SUCCESS' && (
                                <Badge colorScheme="green" variant="subtle">
                                  <HStack spacing={1}>
                                    <Icon as={CheckCircle} />
                                    <Text>Processado</Text>
                                  </HStack>
                                </Badge>
                              )}
                              {upload.status === 'PENDING' && (
                                <Badge colorScheme="yellow" variant="subtle">
                                  <HStack spacing={1}>
                                    <Icon as={Clock} />
                                    <Text>Pendente</Text>
                                  </HStack>
                                </Badge>
                              )}
                              {upload.status === 'STARTED' && (
                                <Badge colorScheme="blue" variant="subtle">
                                  <HStack spacing={1}>
                                    <Spinner size="xs" />
                                    <Text>Processando</Text>
                                  </HStack>
                                </Badge>
                              )}
                              {(upload.status === 'FAILURE' || upload.status === 'REVOKED') && (
                                <Tooltip label={upload.error || 'Erro no processamento'}>
                                  <Badge colorScheme="red" variant="subtle">
                                    <HStack spacing={1}>
                                      <Icon as={XCircle} />
                                      <Text>Erro</Text>
                                    </HStack>
                                  </Badge>
                                </Tooltip>
                              )}
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.500">
                                {new Date(upload.created_at).toLocaleDateString('pt-BR')}
                              </Text>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Modal de Upload */}
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        size="xl"
        closeOnOverlayClick={!isUploading}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enviar Documentos</ModalHeader>
          {!isUploading && <ModalCloseButton />}
          <ModalBody>
            <VStack spacing={5} align="stretch">
              {/* Tipo de Documento */}
              <FormControl isRequired>
                <FormLabel>Tipo de Documento</FormLabel>
                <Select
                  placeholder="Selecione o tipo..."
                  value={tipoDocumento}
                  onChange={e => setTipoDocumento(e.target.value)}
                  isDisabled={isUploading}
                >
                  <option value="departamento_pessoal">Departamento Pessoal</option>
                  <option value="extratos_bancarios">Extratos Bancários</option>
                  <option value="documentos_onboard">Documentos On Board</option>
                </Select>
              </FormControl>

              {/* Área de upload */}
              <FormControl>
                <FormLabel>Arquivos</FormLabel>
                <Box
                  p={6}
                  border="2px dashed"
                  borderColor={isDragging ? 'green.400' : 'gray.300'}
                  borderRadius="lg"
                  bg={isDragging ? 'green.50' : 'gray.50'}
                  textAlign="center"
                  cursor={isUploading ? 'not-allowed' : 'pointer'}
                  transition="all 0.2s"
                  _hover={!isUploading ? { borderColor: 'green.400', bg: 'green.50' } : {}}
                  onDrop={!isUploading ? handleDrop : undefined}
                  onDragOver={!isUploading ? handleDragOver : undefined}
                  onDragLeave={!isUploading ? handleDragLeave : undefined}
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  opacity={isUploading ? 0.6 : 1}
                >
                  <Input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xml,.xlsx,.xls,.csv"
                    display="none"
                    onChange={e => handleFileSelect(e.target.files)}
                    disabled={isUploading}
                  />
                  <VStack spacing={2}>
                    <Icon as={CloudArrowUp} boxSize={10} color="gray.400" />
                    <Text color="gray.600" fontWeight="500">
                      {isDragging
                        ? 'Solte os arquivos aqui...'
                        : 'Arraste arquivos ou clique para selecionar'}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      PDF, XML, Excel (XLSX, XLS), CSV (máx. 50MB por arquivo)
                    </Text>
                  </VStack>
                </Box>
              </FormControl>

              {/* Lista de arquivos */}
              {files.length > 0 && (
                <VStack spacing={2} align="stretch">
                  {files.map(fileUpload => (
                    <Card key={fileUpload.id} variant="outline" size="sm">
                      <CardBody py={2}>
                        <Flex align="center" gap={3}>
                          <Icon as={File} color="gray.500" flexShrink={0} />
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" noOfLines={1}>
                              {fileUpload.file.name}
                            </Text>
                            {fileUpload.status === 'pending' && (
                              <HStack spacing={2} mt={1}>
                                <Icon
                                  as={CalendarBlank}
                                  color="gray.400"
                                  boxSize={4}
                                  flexShrink={0}
                                />
                                <Input
                                  type="month"
                                  size="xs"
                                  value={fileUpload.mesReferencia}
                                  onChange={e =>
                                    handleMesReferenciaChange(fileUpload.id, e.target.value)
                                  }
                                  placeholder="Mês de referência"
                                  isDisabled={isUploading}
                                  bg="white"
                                  borderColor={
                                    !fileUpload.mesReferencia ? 'orange.300' : 'gray.200'
                                  }
                                  _hover={{ borderColor: 'green.300' }}
                                  _focus={{
                                    borderColor: 'green.500',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-green-500)',
                                  }}
                                  maxW="180px"
                                />
                                {!fileUpload.mesReferencia && (
                                  <Text fontSize="xs" color="orange.500" flexShrink={0}>
                                    Obrigatório
                                  </Text>
                                )}
                              </HStack>
                            )}
                            {(fileUpload.status === 'uploading' ||
                              fileUpload.status === 'processing') && (
                              <HStack spacing={2} mt={1}>
                                <Progress
                                  value={fileUpload.progress}
                                  size="xs"
                                  colorScheme="green"
                                  flex={1}
                                />
                                <Text fontSize="xs" color="gray.500" minW="80px">
                                  {getFileStatusText(fileUpload)}
                                </Text>
                              </HStack>
                            )}
                            {fileUpload.status === 'error' && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                {fileUpload.errorMessage}
                              </Text>
                            )}
                            {fileUpload.status === 'success' && (
                              <Text fontSize="xs" color="green.500" mt={1}>
                                Enviado com sucesso!
                              </Text>
                            )}
                          </Box>
                          {fileUpload.status === 'pending' || fileUpload.status === 'error' ? (
                            <IconButton
                              aria-label="Remover arquivo"
                              icon={
                                fileUpload.status === 'error' ? (
                                  <XCircle size={20} weight="fill" />
                                ) : (
                                  <X size={18} weight="bold" />
                                )
                              }
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => removeFile(fileUpload.id)}
                              isDisabled={isUploading}
                            />
                          ) : (
                            getFileStatusIcon(fileUpload)
                          )}
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal} isDisabled={isUploading}>
              Cancelar
            </Button>
            <Button
              colorScheme="green"
              onClick={handleEnviar}
              isLoading={isUploading}
              loadingText="Enviando..."
              leftIcon={<PaperPlaneTilt size={18} />}
              isDisabled={files.length === 0 || !tipoDocumento || files.some(f => !f.mesReferencia)}
            >
              Enviar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SecaoClienteLayout>
  );
};

export default DocumentosEmpresa;
